/**
 * Audio / transcript InputSource kinds.
 * Live tab/mic/virtualCable behavior unchanged; mockStream/audioFile/fixture for harness.
 */
import {
  canUseTabCapture,
  readSelectedVirtualCableInputDeviceId,
  persistSelectedVirtualCableInputDeviceId,
  buildVirtualCableGetUserMediaConstraints,
  pickVbCableSttInputDevice,
} from "./audioSourceManager";

export const INPUT_SOURCE_KINDS = Object.freeze([
  "tab",
  "mic",
  "virtualCable",
  "mockStream",
  "audioFile",
  "fixture",
]);

const MIC_DEVICE_KEY = "CATINTASSIST_MIC_ID";

/**
 * Resolve only a real CABLE Output device. Never fall back to default mic.
 *
 * v4.87.0: always validate the saved ID against live enumeration. Chrome
 * deviceIds are origin-scoped (prod vs localhost:3001 give different IDs),
 * so a saved ID from another origin would otherwise die in getUserMedia
 * with OverconstrainedError and leave the user stuck on tab share.
 * Also prompts once for temp mic permission when labels are blank (fresh
 * origin), otherwise CABLE Output is unfindable by label.
 */
export const resolveVirtualCableInputDeviceId = async ({
  savedDeviceId = readSelectedVirtualCableInputDeviceId(),
  enumerateDevicesFn = navigator.mediaDevices?.enumerateDevices?.bind(navigator.mediaDevices),
  getUserMediaFn = navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices),
} = {}) => {
  const listInputs = async () => {
    if (!enumerateDevicesFn) throw new Error("VB-Cable device list is unavailable.");
    const devices = await enumerateDevicesFn();
    return (devices || []).filter((d) => d.kind === "audioinput");
  };
  let inputs;
  try {
    inputs = await listInputs();
  } catch (_) {
    if (savedDeviceId) return savedDeviceId;
    throw new Error("VB-Cable device list is unavailable.");
  }
  if (savedDeviceId && inputs.some((d) => d.deviceId === savedDeviceId)) return savedDeviceId;
  let deviceId = pickVbCableSttInputDevice(inputs);
  if (!deviceId && inputs.some((d) => !d.label) && getUserMediaFn) {
    try {
      const s = await getUserMediaFn({ audio: true, video: false });
      s.getTracks?.().forEach((t) => t.stop());
    } catch (_) {}
    try {
      inputs = await listInputs();
    } catch (_) {}
    deviceId = pickVbCableSttInputDevice(inputs);
  }
  if (!deviceId) {
    throw new Error("CABLE Output was not found. Choose TAB or reconnect VB-Cable.");
  }
  persistSelectedVirtualCableInputDeviceId(deviceId);
  return deviceId;
};

/** @typedef {'tab'|'mic'|'virtualCable'|'mockStream'|'audioFile'|'fixture'} InputSourceKind */

/**
 * Acquire a MediaStream (or null for fixture mode).
 * @param {InputSourceKind} kind
 * @param {{ fileUrl?: string, loop?: boolean }} [opts]
 * @returns {Promise<{ stream: MediaStream|null, kind: string, mode?: string }>}
 */
export async function acquireInputSource(kind, opts = {}) {
  const source = INPUT_SOURCE_KINDS.includes(kind) ? kind : "tab";

  if (source === "fixture") {
    return { stream: null, kind: "fixture", mode: "fixture" };
  }

  if (source === "mockStream") {
    return { stream: createSilentMockStream(), kind: "mockStream", mode: "mockStream" };
  }

  if (source === "audioFile") {
    const stream = await createAudioFileStream(opts.fileUrl, { loop: opts.loop !== false });
    return { stream, kind: "audioFile", mode: "audioFile" };
  }

  if (source === "mic") {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Microphone requires HTTPS (secure context).");
    }
    let micId = null;
    try {
      micId = localStorage.getItem(MIC_DEVICE_KEY);
    } catch (_) {}
    // v4.143.1: browser DSP muffled the interpreter's voice — keep raw mic,
    // same as every other mic path (GreetingsPanel, AudioSettingsContext).
    const withProcessing = {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    };
    try {
      const audio = micId
        ? { deviceId: { exact: micId }, ...withProcessing }
        : true;
      const stream = await navigator.mediaDevices.getUserMedia({ audio });
      return { stream, kind: "mic" };
    } catch (err) {
      // Mobile deviceIds go stale; exact constraint → silent OverconstrainedError.
      if (
        micId &&
        (err?.name === "OverconstrainedError" || err?.name === "NotFoundError")
      ) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: withProcessing,
        });
        return { stream, kind: "mic" };
      }
      throw err;
    }
  }

  if (source === "virtualCable") {
    const tryAcquire = (deviceId) =>
      navigator.mediaDevices.getUserMedia(buildVirtualCableGetUserMediaConstraints(deviceId));
    const deviceId = await resolveVirtualCableInputDeviceId();
    try {
      const stream = await tryAcquire(deviceId);
      return { stream, kind: "virtualCable" };
    } catch (err) {
      if (err?.name !== "OverconstrainedError" && err?.name !== "NotFoundError") throw err;
      // Stale exact deviceId (e.g. saved on another origin) — drop it,
      // re-pick CABLE Output by label once, and retry. Never fall to default mic.
      persistSelectedVirtualCableInputDeviceId("");
      const freshId = await resolveVirtualCableInputDeviceId({ savedDeviceId: "" });
      const stream = await tryAcquire(freshId);
      return { stream, kind: "virtualCable" };
    }
  }

  // Default: tab share
  if (!canUseTabCapture()) {
    const ex =
      typeof DOMException !== "undefined"
        ? new DOMException("Tab capture not supported in this browser", "NotSupportedError")
        : Object.assign(new Error("Tab capture not supported in this browser"), {
            name: "NotSupportedError",
          });
    throw ex;
  }
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
  return { stream, kind: "tab" };
}

/** Silent MediaStream — no getUserMedia / getDisplayMedia permission prompt. */
export function createSilentMockStream() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) {
    throw new Error("AudioContext unavailable for mockStream");
  }
  const ctx = new AC();
  const dest = ctx.createMediaStreamDestination();
  // Keep context alive with a near-silent oscillator (muted gain).
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  gain.gain.value = 0.0001;
  osc.connect(gain);
  gain.connect(dest);
  osc.start();
  const stream = dest.stream;
  stream.__catintMockCleanup = () => {
    try {
      osc.stop();
    } catch (_) {}
    try {
      ctx.close();
    } catch (_) {}
  };
  return stream;
}

/**
 * Loop an audio file into a MediaStream (optional live STT attach).
 * @param {string} fileUrl
 */
export async function createAudioFileStream(fileUrl, { loop = true } = {}) {
  if (!fileUrl) throw new Error("audioFile requires fileUrl");
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) throw new Error("AudioContext unavailable for audioFile");

  const audio = new Audio(fileUrl);
  audio.crossOrigin = "anonymous";
  audio.loop = loop;
  await audio.play().catch(() => {});
  const ctx = new AC();
  const src = ctx.createMediaElementSource(audio);
  const dest = ctx.createMediaStreamDestination();
  src.connect(dest);
  // Also connect to destination so element can play if needed (gain 0 to avoid local echo).
  const gain = ctx.createGain();
  gain.gain.value = 0;
  src.connect(gain);
  gain.connect(ctx.destination);

  const stream = dest.stream;
  stream.__catintMockCleanup = () => {
    try {
      audio.pause();
    } catch (_) {}
    try {
      ctx.close();
    } catch (_) {}
  };
  return stream;
}

/** Map legacy useDeepgram source strings → InputSourceKind. */
export function mapLegacySourceToKind(source) {
  if (source === "mic") return "mic";
  if (source === "virtualCable") return "virtualCable";
  return "tab";
}
